/* External */
import {
  createContext, useContext, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import MqttComponent from '@dtvgo/mqtt';

/* Utils */
import platform from '~/utils/platform';
import { authInterceptor, refreshIdToken } from '../../services/authService';
import { getAccessTokenData, getUserAccountFromIdToken } from '../../utils/accessTokenData';
import {
  APP_THEME,
  ENVIRONMENT,
  MQTT_FLAG,
  MQTT_REALTIME_ENTITLEMENT_FLAG,
} from '../../utils/env';

/* Other */
import { useIsLoggedIn } from '../UserProvider';

const MqttContext = createContext();

async function initializeMqtt() {
  let auth;
  let client;
  const host = import.meta.env.VITE_BACKEND_SERVICE_HOST;
  const mqttComponentInstance = new MqttComponent(authInterceptor);
  const { idToken } = await getAccessTokenData();
  const { customerId, iat } = getUserAccountFromIdToken(idToken);

  try {
    auth = await mqttComponentInstance.authMqtt(
      idToken,
      host,
      APP_THEME,
      platform.appName, // clientId
      ENVIRONMENT,
    );
    client = await mqttComponentInstance.connectMqtt(
      auth.clientId,
      auth.url,
      customerId,
      (iat * 1000), // miliseconds
      refreshIdToken,
      MQTT_REALTIME_ENTITLEMENT_FLAG,
      MQTT_FLAG,
    );
  } catch (error) {
    console.error(error);
  }

  return client;
}

function MqttProvider({ children }) {
  const [contextValue, setContextValue] = useState('');
  const isLoggedIn = useIsLoggedIn();

  useEffect(() => {
    const initialize = async () => {
      try {
        if (MQTT_FLAG && isLoggedIn) {
          const result = await initializeMqtt();
          setContextValue(result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    initialize();
  }, [isLoggedIn]);

  return (
    <MqttContext.Provider value={contextValue}>
      {children}
    </MqttContext.Provider>
  );
}

export default MqttProvider;
export const useMqttContext = () => useContext(MqttContext);
export { MqttContext };

MqttProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
